// Hackathons: announced a day ahead, two days of building, then judging against NPC teams.
// Register (DGN gas), push build sessions (up to 3 a day), and either place or go home with lessons.

import { TUNABLES } from "@degen/shared/config/tunables";
import { money } from "@degen/shared/money";
import type { Rng } from "../rng";
import { dayOf, pushLog, type GameState } from "../state";
import { addTrust } from "../player";

const H = TUNABLES.hackathons;

const PREFIX = ["Degen", "Island", "Moonshot", "Based", "Ape Labs", "Gasless", "Ser", "Night Shift", "Trenches", "Frens"];
const SUFFIX = ["Hack Week", "Buildathon", "Hackfest", "Jam", "Sprint", "Hack House"];
const THEMES = [
  { theme: "AI agents", tracks: ["Trading copilot", "Scam-DM detector", "Onchain butler"] },
  { theme: "Memecoin tooling", tracks: ["Rug checker", "Launch analytics", "Holder heatmaps"] },
  { theme: "Onchain games", tracks: ["Idle game", "PvP arena", "Pet battler"] },
  { theme: "Payments for everyone", tracks: ["Remittances", "Merchant checkout", "Split bills"] },
  { theme: "DeFi safety", tracks: ["Wallet firewall", "Approval cleaner", "Exploit alerts"] },
  { theme: "Creator economy", tracks: ["Fan clubs", "Tip jars", "NFT tickets"] },
];

export interface HackEntry {
  track: string;
  points: number;
  builds: number;
  buildsToday: number;
  buildDay: number;
}

export interface Hackathon {
  id: string;
  name: string;
  theme: string;
  tracks: string[];
  startDay: number; // building runs startDay..endDay-1, judged the morning of endDay
  endDay: number;
  prize: number;
  teams: number; // NPC teams
  hotTrack: string; // the judges' favourite (worth more)
  entry?: HackEntry;
  result?: { place: number; prize: number; teams: number };
}

export interface HackathonsState {
  list: Hackathon[];
  nextId: number;
  skill: number; // hackathons you've finished (you get better)
  wins: number;
}

export function createHackathons(): HackathonsState {
  return { list: [], nextId: 1, skill: 0, wins: 0 };
}

export type HackStatus = "upcoming" | "live" | "judged";
export function hackStatus(h: Hackathon, day: number): HackStatus {
  return h.result || day >= h.endDay ? "judged" : day >= h.startDay ? "live" : "upcoming";
}

type Result = { ok: true; data?: Record<string, number> } | { ok: false; error: string };

export function joinHackathon(state: GameState, id: string, track: string): Result {
  const h = state.hackathons.list.find((x) => x.id === id);
  const day = dayOf(state.tick);
  if (!h || hackStatus(h, day) === "judged") return { ok: false, error: "That hackathon is over." };
  if (h.entry) return { ok: false, error: "You're already registered." };
  if (!h.tracks.includes(track)) return { ok: false, error: "Pick a track." };
  h.entry = { track, points: 0, builds: 0, buildsToday: 0, buildDay: 0 };
  pushLog(state, "hackathon", `🧑‍💻 Registered for ${h.name} (${track}). Build at the Hackathon Hub or from the Hackr app.`);
  return { ok: true };
}

export function hackBuild(state: GameState, rng: Rng, id: string): Result {
  const h = state.hackathons.list.find((x) => x.id === id);
  const day = dayOf(state.tick);
  if (!h?.entry) return { ok: false, error: "Register first." };
  if (hackStatus(h, day) !== "live") return { ok: false, error: day < h.startDay ? "Building starts on day " + h.startDay + "." : "Submissions are closed." };
  const e = h.entry;
  if (e.buildDay !== day) {
    e.buildDay = day;
    e.buildsToday = 0;
  }
  if (e.buildsToday >= H.buildsPerDay) return { ok: false, error: "You've shipped enough today. Sleep, then keep building." };
  const skill = 1 + H.skillBonus * Math.min(10, state.hackathons.skill);
  const fit = e.track === h.hotTrack ? H.hotTrackBonus : 1;
  const pts = rng.range(H.buildPoints[0], H.buildPoints[1]) * skill * fit;
  e.points += pts;
  e.builds++;
  e.buildsToday++;
  return { ok: true, data: { points: Math.round(pts), total: Math.round(e.points) } };
}

function makeHackathon(state: GameState, rng: Rng, day: number): Hackathon {
  const t = rng.pick(THEMES);
  const startDay = day + H.announceDaysBefore;
  return {
    id: `hk${state.hackathons.nextId++}`,
    name: `${rng.pick(PREFIX)} ${rng.pick(SUFFIX)}`,
    theme: t.theme,
    tracks: [...t.tracks],
    startDay,
    endDay: startDay + H.durationDays,
    prize: Math.round(rng.range(H.prize[0], H.prize[1]) / 500) * 500,
    teams: rng.int(H.teams[0], H.teams[1]),
    hotTrack: rng.pick(t.tracks),
  };
}

/** Day boundary: judge finished hackathons, announce new ones. */
export function hackathonsNewDay(state: GameState, rng: Rng, day: number) {
  const hs = state.hackathons;
  for (const h of hs.list) {
    if (h.result || day < h.endDay) continue;
    // NPC teams are scored even if you didn't enter (keeps the RNG path stable).
    const npc = Array.from({ length: h.teams }, () => H.npcMeanPoints + rng.normal() * H.npcSdPoints);
    const demo = rng.range(H.demoDayLuck[0], H.demoDayLuck[1]); // the demo either lands or it doesn't
    if (!h.entry) {
      h.result = { place: 0, prize: 0, teams: h.teams };
      continue;
    }
    const mine = h.entry.points * demo;
    const place = mine <= 0 ? h.teams + 1 : 1 + npc.filter((x) => x > mine).length;
    const prize = place <= H.split.length ? Math.round(h.prize * H.split[place - 1]) : 0;
    h.result = { place, prize, teams: h.teams + 1 };
    const p = state.player;
    if (mine > 0) hs.skill++;
    if (prize > 0) {
      p.cash += prize;
      p.followers += place === 1 ? H.followers.win : H.followers.podium;
      addTrust(p, place === 1 ? H.trust.win : H.trust.podium);
      if (place === 1) hs.wins++;
      pushLog(state, "hackathon_won", `🏆 ${h.name}: you placed #${place} of ${h.teams + 1}! +${money(prize)}, and the timeline noticed.`);
    } else if (mine > 0 && place <= Math.ceil((h.teams + 1) * 0.15)) {
      p.followers += H.followers.mention;
      pushLog(state, "hackathon", `🎖️ ${h.name}: honorable mention (#${place} of ${h.teams + 1}). No prize, but people saw your demo.`);
    } else {
      pushLog(state, "hackathon", mine > 0 ? `🫠 ${h.name}: #${place} of ${h.teams + 1}. No prize this time. You learned a lot though (+skill).` : `${h.name}: you registered but never shipped. The judges never saw it.`);
    }
  }
  if (day >= H.firstAnnounceDay && (day - H.firstAnnounceDay) % H.everyDays === 0) {
    const h = makeHackathon(state, rng, day);
    hs.list.push(h);
    pushLog(state, "hackathon_announced", `🧑‍💻 ${h.name} (${h.theme}) starts day ${h.startDay}. ${money(h.prize)} prize pool, ${H.entryDgn} DGN to enter.`);
  }
  if (hs.list.length > 12) hs.list.splice(0, hs.list.length - 12);
}
